import React, { useState } from "react";
import gql from "graphql-tag";
import { useQuery } from "react-apollo";
import isEmpty from "lodash/isEmpty";
import { oc } from "ts-optchain";
import { Link } from "@reach/router";
import { useLoadingDelay, useStoreAppValue } from "@hooks";
import { Text, Box } from "@atoms";
import { RailsUrl, constants } from "@services";
import { BrandBlock } from "../../brandBlockComponents";
import { CollectionNearbyBrandsTitle } from "../CollectionNearbyBrandsTitle";
import { CollectionRenderFragment } from "../CollectionRender/__generated__/CollectionRenderFragment";
import CollectionNearbyBrands from "./CollectionNearbyBrands";
import { CollectionNearbyBrandsQuery as TCollectionNearbyBrandsQuery } from "./__generated__/CollectionNearbyBrandsQuery";

// == Types ================================================================
interface IProps {
  collection: CollectionRenderFragment;
}

// == Constants ============================================================

const COLLECTION_NEARBY_BRANDS_QUERY = gql`
  query CollectionNearbyBrandsQuery($coordinates: CoordinatesInput) {
    BrandSearch(linkType: EMPYR, coordinates: $coordinates, first: 15) {
      edges {
        cursor
        node {
          id
          ...BrandBlockFragment
        }
      }
    }
  }
  ${BrandBlock.fragments.fields}
`;

// == Component ============================================================

export default function CollectionNearbyBrandsQuery({ collection }: IProps) {
  const { campaignId } = useStoreAppValue();

  const [coords, setCoords] = useState(null);
  const [findMe, setFindMe] = useState(false);
  const loadingDelayReady = useLoadingDelay();
  const onGetCurrentPosition = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(setCoordinates);
      setFindMe(true);
    }
    return null;
  };

  const queryVariables = () => {
    if (coords) {
      return { coordinates: { ...coords } };
    }
    return null;
  };

  const setCoordinates = (position) => {
    const lng = position.coords.longitude;
    const lat = position.coords.latitude;
    setCoords({ latitude: lat, longitude: lng, source: "CURRENT" });
    return coords;
  };

  const { loading, data, error } = useQuery<TCollectionNearbyBrandsQuery>(
    COLLECTION_NEARBY_BRANDS_QUERY,
    {
      variables: queryVariables(),
    }
  );

  if (error) {
    return <Text>{error.message}</Text>;
  }

  if ((isEmpty(data) && loading) || !loadingDelayReady) return null;
  const nearbyBrands = oc(data).BrandSearch.edges();
  if (!nearbyBrands || (nearbyBrands.length < 1 && !findMe)) return null;
  return (
    <>
      {!!collection.showTitle && (
        <CollectionNearbyBrandsTitle
          collection={collection}
          loading={loading}
          onGetCurrentPosition={onGetCurrentPosition}
        />
      )}
      {nearbyBrands.length >= 1 ? (
        <CollectionNearbyBrands nearbyBrands={nearbyBrands} />
      ) : (
        <Box>
          <Text>
            Sorry there are no nearby stores,{" "}
            <Link to={RailsUrl.brandsUrl(campaignId)}>click here</Link> to view all brands
          </Text>
        </Box>
      )}
    </>
  );
}

// == Styles ===============================================================
